import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Header } from "./components/Header";
import { Footer } from "./components/Footer";
import { WhatsAppButton } from "./components/WhatsAppButton";
import { Home } from "./pages/Home";
import LearningPaths from "./pages/LearningPaths";
import Programs from './pages/Programs';
import ActivateAccount from './pages/ActivateAccount';
import About from "./pages/About";
import FAQ from "./pages/FAQ";
import { Apply } from "./pages/Apply";
import { Contact } from "./pages/Contact";
import { Consultation } from "./pages/Consultation";
import { JoinNetwork } from "./pages/JoinNetwork";
import { Admin } from "./pages/Admin";
import { ForStudents } from "./pages/ForStudents";
import { ForEmployers } from "./pages/ForEmployers";
import { ForColleges } from "./pages/ForColleges";
import { CareerReadinessCheck } from "./pages/CareerReadinessCheck";
import { Careers } from "./pages/Careers";
import { Dehradun } from "./pages/Dehradun";
import { DehradunCheck } from "./pages/DehradunCheck";
import { ScanRedirect, JoinRedirect } from "./pages/Redirects";
import EventRegistration from "./pages/EventRegistration";
import { ShortLink } from './components/ShortLink';
import { captureUTMParams } from "./utils/utm";
import { captureReferral } from "./utils/referral";
import JourneyStudent from "./pages/journey/JourneyStudent";
import JourneyAdmin from "./pages/journey/JourneyAdmin";
import { JourneyAuthCallback } from "./pages/journey/JourneyAuthCallback";

// CareerOS imports
import { AuthProvider } from "./context/AuthContext";
import Login from "./pages/app/Login";
import Register from "./pages/app/Register";
import Dashboard from "./pages/app/Dashboard";
import Profile from "./pages/app/Profile";
import RoleSelect from "./pages/app/RoleSelect";
import ActionPlan from "./pages/app/ActionPlan";
import Assessment from "./pages/app/Assessment";

// Layout wrapper to conditionally show Header/Footer
function Layout({ children }) {
  const location = useLocation();
  useEffect(() => { if (!location.hash) window.scrollTo(0, 0); }, [location.pathname, location.hash]);
  const isAdminPage = location.pathname === '/admin';
  const isCareerCheckPage = location.pathname === '/career-readiness-check';
  const isDehradunPage = location.pathname.startsWith('/dehradun');
  const isEventPage = location.pathname === '/session';
  const isCareerOSPage = location.pathname.startsWith('/app');
  const isJourneyPage = location.pathname.startsWith('/demo') || location.pathname === '/my-journey';
  
  // Capture UTM params on route change
  useEffect(() => {
    captureUTMParams();
    captureReferral(process.env.REACT_APP_BACKEND_URL);
  }, [location.search]);

  // CareerOS pages have their own layout
  if (isJourneyPage) {
    if (location.hash?.includes('session_id=')) return <JourneyAuthCallback />;
    return <main>{children}</main>;
  }

  if (isCareerOSPage) {
    return <main>{children}</main>;
  }

  return (
    <>
      {!isAdminPage && !isCareerCheckPage && !isDehradunPage && !isEventPage && <Header />}
      <main>{children}</main>
      {!isAdminPage && !isCareerCheckPage && !isDehradunPage && !isEventPage && <Footer />}
      {!isAdminPage && !isCareerCheckPage && !isDehradunPage && !isEventPage && <WhatsAppButton />}
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <div className="App">
          <BrowserRouter>
            <Layout>
              <Routes>
                {/* Main Website Routes */}
                <Route path="/" element={<Home />} />
                <Route path="/for-students" element={<ForStudents />} />
                <Route path="/for-colleges" element={<ForColleges />} />
                <Route path="/for-employers" element={<ForEmployers />} />
                <Route path="/about" element={<About />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/journey" element={<About />} />
                <Route path="/apply" element={<Apply />} />
                <Route path="/c/:code" element={<ShortLink to="assessment" />} />
                <Route path="/book" element={<ShortLink to="booking" />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/consultation" element={<Consultation />} />
                <Route path="/network" element={<JoinNetwork />} />
                <Route path="/learning-paths" element={<LearningPaths />} />
                <Route path="/programs" element={<Programs />} />
                <Route path="/activate" element={<ActivateAccount />} />
                <Route path="/career-readiness-check" element={<CareerReadinessCheck />} />
                <Route path="/careers" element={<Careers />} />
                <Route path="/dehradun" element={<Dehradun />} />
                <Route path="/dehradun/check" element={<DehradunCheck />} />
                <Route path="/scan" element={<ScanRedirect />} />
                <Route path="/join" element={<JoinRedirect />} />
                <Route path="/session" element={<EventRegistration />} />
                <Route path="/admin" element={<Admin />} />
                <Route path="/demo" element={<JourneyStudent />} />
                <Route path="/my-journey" element={<JourneyStudent />} />
                <Route path="/dashboard" element={<Navigate to="/my-journey" replace />} />
                <Route path="/demo/admin" element={<JourneyAdmin />} />
                
                {/* CareerOS Routes (Soft Launch) */}
                <Route path="/app" element={<Navigate to="/app/login" replace />} />
                <Route path="/app/login" element={<Login />} />
                <Route path="/app/register" element={<Register />} />
                <Route path="/app/dashboard" element={<Dashboard />} />
                <Route path="/app/profile" element={<Profile />} />
                <Route path="/app/assessment" element={<Assessment />} />
                <Route path="/app/role-select" element={<RoleSelect />} />
                <Route path="/app/action-plan" element={<ActionPlan />} />
              </Routes>
            </Layout>
          </BrowserRouter>
        </div>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
